import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { X } from 'lucide-react';
import { productService } from '../../services/productService';

const POPUP_STORAGE_KEY = 'fazlic_sale_popup_last_seen';

function todayKey() {
  return new Date().toISOString().slice(0, 10);
}

export const SalePopup = () => {
  const [open, setOpen] = useState(false);
  const [products, setProducts] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);

  const featured = useMemo(() => products.slice(0, 3), [products]);
  const activeProduct = featured[activeIndex] || null;

  useEffect(() => {
    const lastSeen = localStorage.getItem(POPUP_STORAGE_KEY);
    if (lastSeen === todayKey()) return;

    const timer = window.setTimeout(async () => {
      try {
        const result = await productService.getPopupProducts();
        const data = Array.isArray(result?.data) ? result.data : [];
        if (data.length === 0) return;
        setProducts(data);
        setActiveIndex(0);
        setOpen(true);
      } catch {
        // Popup is non-blocking. Ignore fetch errors.
      }
    }, 5000);

    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!open || featured.length <= 1) return;
    const intervalId = window.setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % featured.length);
    }, 2000);
    return () => window.clearInterval(intervalId);
  }, [open, featured.length]);

  const handleClose = () => {
    localStorage.setItem(POPUP_STORAGE_KEY, todayKey());
    setOpen(false);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/40 p-3 sm:p-4">
      <div className="w-full max-w-lg rounded-2xl border border-border bg-card shadow-elevated">
        <div className="flex items-start justify-between gap-3 border-b border-border px-4 py-3">
          <div>
            <p className="font-serif text-xl text-foreground">Proizvodi na akciji</p>
            <p className="text-xs font-ui text-muted-foreground mt-1">Izdvojili smo trenutnu ponudu.</p>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-border text-muted-foreground hover:text-foreground hover:bg-muted"
            aria-label="Zatvori akcijsku ponudu"
          >
            <X size={16} />
          </button>
        </div>

        <div className="p-4">
          {activeProduct && (
            <Link
              key={activeProduct.id}
              to={`/proizvodi/${activeProduct.id}`}
              onClick={handleClose}
              className="flex items-center gap-3 rounded-xl border border-border p-2.5 hover:bg-muted/50 transition-colors"
            >
              <img
                src={activeProduct.primaryImageUrl}
                alt={activeProduct.name}
                className="h-20 w-20 rounded-lg object-cover border border-border"
              />
              <div className="min-w-0 flex-1">
                <p className="font-ui text-base font-semibold text-foreground truncate">{activeProduct.name}</p>
                <p className="font-ui text-sm text-muted-foreground">
                  {Number(activeProduct.price || 0).toLocaleString('sr-RS')} RSD / {activeProduct.unit}
                </p>
              </div>
            </Link>
          )}
          {featured.length > 1 && (
            <div className="mt-3 flex items-center justify-center gap-1.5">
              {featured.map((_, idx) => (
                <span
                  key={idx}
                  className={`h-1.5 w-6 rounded-full transition-colors ${
                    idx === activeIndex ? 'bg-primary' : 'bg-muted'
                  }`}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
